/*
 * This component is copied from Backstage 1.8.2.
 * CHANGES:
 * - Added custom UI
 * - Add useEffect to allow filter clear
 */

import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityOwnerFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { AsyncEnumFilter } from '../WbTableFilters';

export enum OwnerKind {
  AccessControlRequestTemplate = 'accesscontrolrequesttemplate',
  Blueprint = 'blueprint',
  Domain = 'domain',
  EditTemplate = 'edittemplate',
  Resource = 'resource',
  ReverseProvisioningTemplate = 'reverseprovisioningtemplate',
  System = 'system',
  SystemPrototypeTemplate = 'systemprototypetemplate',
  Template = 'template',
}

type Props = {
  kind: OwnerKind;
};

export const EntityOwnerPicker: FC<Props> = props => {
  const {
    updateFilters,
    filters,
    queryParameters: { owners: ownersParameter },
  } = useEntityList();
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const queryParamOwners = useMemo(
    () => [ownersParameter].flat().filter(Boolean) as string[],
    [ownersParameter],
  );

  const [selectedOwners, setSelectedOwners] = useState(
    queryParamOwners.length ? queryParamOwners : filters.owners?.values ?? [],
  );

  // Set selected owners on query parameter updates; this happens at initial page load and from
  // external updates to the page location.
  useEffect(() => {
    if (queryParamOwners.length) {
      setSelectedOwners(queryParamOwners);
    }
  }, [queryParamOwners]);

  const getAvailableOwners = useCallback(async () => {
    const credentials = await identityApi.getCredentials();
    const facet = 'spec.owner';
    const { facets } = await catalogApi.getEntityFacets(
      {
        facets: [facet],
        filter: [
          {
            kind: [props.kind],
          },
        ],
      },
      credentials,
    );

    return facets[facet].map(record => record.value);
  }, [catalogApi, identityApi, props.kind]);

  useEffect(() => {
    updateFilters({
      owners: selectedOwners.length
        ? new EntityOwnerFilter(selectedOwners)
        : undefined,
    });
  }, [selectedOwners, updateFilters]);

  return (
    <AsyncEnumFilter<string>
      field="Owner"
      onChange={value => setSelectedOwners(value ?? [])}
      getOptions={getAvailableOwners}
      renderOption={o => o}
      renderValue={v => v}
      value={selectedOwners}
      onSearch={(searchValue, options) =>
        options.filter(o => new RegExp(searchValue, 'ig').test(o))
      }
    />
  );
};
